// @vue/tsconfig задаёт types: [], поэтому автоподключение @types отключено и
// глобальный namespace Gtag приходится подключать явно.
/// <reference types="gtag.js" />

declare global {
  interface Window {
    gtag: Gtag.Gtag;
  }
}

const debug = (...args: Array<unknown>) => {
  if (import.meta.env.VITE_RUNTIME_ENV === 'development') {
    window.console.info('[GTAG]', ...args);
  }
};

/**
 * Defers `cb` until the main thread is idle so the heavy `gtag/js` download
 * (~175 KB) does not compete with the Vue app and its LCP element during
 * initial paint. Falls back to a short timeout where `requestIdleCallback` is
 * unavailable (older WebViews, jsdom).
 */
const scheduleWhenIdle = (cb: () => void) => {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(cb, { timeout: 3500 });
    return;
  }
  window.setTimeout(cb, 200);
};

export const init = (gaTagId: string, debugMode: boolean) => {
  /**
   * The `gtag` shim and its `dataLayer` queue are installed synchronously so
   * `sendEvent` callers (e.g. the main-page-open event fired on mount) always
   * have a `window.gtag` to call; their events buffer in `dataLayer` and are
   * flushed once `gtag/js` finishes loading. Only the network download of
   * `gtag/js` itself is deferred off the critical render path.
   */
  const setupScript = document.createElement('script');
  setupScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gaTagId}', { 'debug_mode': ${debugMode} });
    `;
  document.head.prepend(setupScript);

  scheduleWhenIdle(() => {
    const loadingScript = document.createElement('script');
    loadingScript.async = true;
    loadingScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaTagId}`;
    document.head.prepend(loadingScript);
    debug('gtag/js loaded');
  });

  debug('Client initialized');
};

type Primitive = string | number | symbol | bigint | boolean | null | undefined;
type GoogleAnalyticsParams = Record<string, Primitive | Primitive[]>;

/**
 * @param {string} eventName - name of the event.
 * @param {GoogleAnalyticsParams} params - should always be an object, otherwise the event will not be sent.
 * Every params value is transformed into String, so should not send Object as params value since it will be [Object object].
 * */
export const sendEvent = (
  eventName: string,
  params?: GoogleAnalyticsParams,
) => {
  debug('sendEvent', eventName, params);
  window.gtag('event', eventName, params);
};
